import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DynamicTable from "../../../components/lab-dynamic-table";
import axios from "axios";
import { toast } from "react-toastify";
import useDebounce from "../../../hooks/useDebounce";
import {
  Add,
  Delete,
  Edit,
  FileDownload,
  Print,
  Search,
  Receipt,
} from "@mui/icons-material";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useSelector } from "react-redux";
import DateRangeSelector from "../../../components/DateRangeSelector";



// Reusable button styles
const gradientButtonStyle = {
  minWidth: { xs: "auto", md: "80px" },
  fontSize: { xs: "12px", md: "12px" },
  fontWeight: "600",
  textTransform: "none",
  borderRadius: "8px",
  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
  color: "#ffffff",
  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
    transform: "translateY(-1px)",
  },
};

// --- Transaction Tab Content ---
const TransactionTab = () => {
  const nowDate = new Date().toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(nowDate);
  const [toDate, setToDate] = useState(nowDate);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(100);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transactionData, setTransactionData] = useState<any[]>([]);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [deleteReason, setDeleteReason] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [totalAmounts, setTotalAmounts] = useState<any>({});
  const [users, setUsers] = useState<any[]>([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<any | null>(null);

  const [searchParams] = useSearchParams();

  const formatDateTime = (date: Date | null) => {
    if (!date) return "";
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
      date.getDate()
    )}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // --- Search functionality with debounce ---
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchInput, 1000);

  const profile = useSelector((state: any) => state.profileSlice.profile);
  const ProcedurePermissions =
    profile?.data?.accessConfigurations?.Account || {};
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );

  const [openPrintDialogPayment, setOpenPrintDialogPayment] = useState(false);
  const navigate = useNavigate();

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
    setPage(0);
  }, [debouncedSearchTerm]);

  // Check URL query parameter for payment mode
  useEffect(() => {
    const pmParam = searchParams.get("pm");
    if (pmParam === "cash") {
      setPaymentMode("Cash");
    } else if (pmParam === "online") {
      setPaymentMode("Online");
    } else {
      setPaymentMode("");
    }
  }, [searchParams]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCount(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchUsers = async (): Promise<void> => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/findAllUsers`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setUsers(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchTransactionData = async (): Promise<void> => {
    setLoading(true);
    try {
      const params: any = { fromDate, toDate, pageNo: page + 1, count: count };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (paymentMode) params.payment_mode = paymentMode;
      if (selectedCreatedBy) params.userId = selectedCreatedBy._id;

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getPatientTransactions`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response.data.isSuccess) {
        setTransactionData(response.data.data.transaction || []);
        setTotalAmounts(response.data.data.total || {});
        setTotal(response.data.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch transaction data");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching transaction data");
      setTransactionData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactionData();
  }, [fromDate, toDate, searchTerm, paymentMode, page, count, selectedCreatedBy]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = () => {
    setPage(0);
    setSearchTerm(searchInput);
    fetchTransactionData();
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setDeleteReason("");
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return toast.error("Authorization token is missing");

      const config: any = {
        headers: { Authorization: `Bearer ${token}` },
      };
      if (deleteReason) config.data = { deleteReason };

      const response = await axios.delete(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/deleteIncomeById/${selectedInvoiceId}`,
        config
      );

      if (!response.data.isSuccess)
        throw new Error(response.data.message || "Failed to delete");
      toast.success(response.data.message);
      fetchTransactionData();
    } catch (error: any) {
      toast.error(error.message);
    }
    handleDeleteModalClose();
  };

  const handleExcelExport = () => {
    if (!transactionData.length) return toast.warn("No data to export.");

    const worksheetData = transactionData.map(item => ({
      "INVOICE#": item.invoiceInfo?.invoiceId || "",
      "MR#": item.patientInfo?.mrn || "",
      "PATIENT NAME": item.patientInfo?.patientName || "",
      "DESCRIPTION": item.description || item.invoiceInfo?.description || "",
      "PAYMENT MODE": item.paymentMode || "",
      "RECEIVED BY": item.receivedBy || "",
      "CREATED AT": formatDateTime(new Date(item.createdAt)),
      "PAYMENT": item.payment || 0,
    }));

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Transactions");
    xlsx.writeFile(workbook, `Transactions_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!transactionData.length) return toast.warn("No data to print.");

    const doc = new jsPDF();
    doc
      .setFontSize(16)
      .setFont("helvetica", "bold")
      .text("Transactions Report", doc.internal.pageSize.getWidth() / 2, 20, {
        align: "center",
      });
    doc
      .setFontSize(10)
      .setFont("helvetica", "normal")
      .text(
        `Report for period: ${fromDate} to ${toDate}`,
        doc.internal.pageSize.getWidth() / 2,
        28,
        { align: "center" }
      );

    const head = [
      [
        "INVOICE#",
        "MR#",
        "PATIENT NAME",
        "DESCRIPTION",
        "PAYMENT MODE",
        "RECEIVED BY",
        "CREATED AT",
        "PAYMENT",
      ],
    ];
    const body = transactionData.map(item => [
      item.invoiceInfo?.invoiceId ?? "",
      item.patientInfo?.mrn ?? "",
      item.patientInfo?.patientName ?? "",
      item.description || (item.invoiceInfo?.description ?? ""),
      item.paymentMode ?? "",
      item.receivedBy ?? "",
      formatDateTime(new Date(item.createdAt)),
      Number(item.payment || 0).toFixed(2),
    ]);

    autoTable(doc, {
      startY: 35,
      head,
      body,
      headStyles: { fillColor: [22, 160, 133] },
      columnStyles: {
        7: { halign: "right" },
      },
      styles: { fontSize: 8 },
    });
    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Table data without actions
  const tableData = transactionData?.map((item: any) =>{ return [
    item.invoiceInfo?.invoiceId || "",
    item.patientInfo?.mrn || "",
    item.patientInfo?.patientName || "",
    item.description || item.invoiceInfo?.description || "",
    item.paymentMode || "",
    item.receivedBy || "",
    formatDateTime(new Date(item.createdAt)),
    item.payment || 0,
  ]});

  // Use tableData directly without totals row
  const tableDataWithTotals = tableData;

  return (
    <Box>
      <Box
        display="flex"
        sx={{ background: "#ffffff", borderRadius: "8px" }}
        justifyContent="space-between"
        alignItems="end"
        paddingY={1}
        paddingX={1}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" gap={1}>
          <Tooltip
            title={
              ProcedurePermissions?.view_module
                ? "Export data to Excel file"
                : "No permission to export"
            }
            arrow
            placement="top"
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={
                ProcedurePermissions?.view_module
                  ? handleExcelExport
                  : () => toast.warn("No permission")
              }
              disabled={!ProcedurePermissions?.view_module}
              sx={{
                ...gradientButtonStyle,
                opacity: ProcedurePermissions?.view_module ? 1 : 0.5,
              }}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip
            title={
              ProcedurePermissions?.view_module
                ? "Print transaction report"
                : "No permission to print"
            }
            arrow
            placement="top"
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={
                ProcedurePermissions?.view_module
                  ? handlePrint
                  : () => toast.warn("No permission")
              }
              disabled={!ProcedurePermissions?.view_module}
              sx={{
                ...gradientButtonStyle,
                opacity: ProcedurePermissions?.view_module ? 1 : 0.5,
              }}
            >
              Print
            </Button>
          </Tooltip>
        </Box>

        <Box
          display="flex"
          gap={1.5}
          alignItems="flex-end"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          flexGrow={1}
        >
        
          <Box sx={{ width: { xs: "100%", md: "160px" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Search by Pt name or MRN
            </Typography>
            <TextField
              className="custom-input"
              size="small"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleSearch()}
              placeholder="Enter search value..."
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Payment Mode
            </Typography>
            <Select
              size="small"
              value={paymentMode}
              onChange={e => setPaymentMode(e.target.value)}
              displayEmpty
              sx={{ minWidth: { xs: "50px", md: "100px" } }}
              className="custom-input"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
            </Select>
          </Box>
          <Box sx={{ width: { xs: "100%", md: "120px" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Created By
            </Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={users}
              sx={{
                background: "#F5F5FB",
                borderRadius: "12px",
                "& fieldset": { border: "none" },
              }}
              getOptionLabel={option => option.fullName || ""}
              value={selectedCreatedBy}
              onChange={(e, newValue) => {
                setSelectedCreatedBy(newValue);
              }}
              renderInput={params => (
                <TextField {...params} placeholder="Select User" />
              )}
              clearOnEscape
            />
          </Box>
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box paddingX={1} sx={{ marginTop: "16px" }}>
        <DynamicTable
          headers={[
            "INVOICE#",
            "MR#",
            "PATIENT NAME",
            "DESCRIPTION",
            "PAYMENT MODE",
            "RECEIVED BY",
            "CREATED AT",
            "PAYMENT",
          ]}
          loading={loading}
          data={tableDataWithTotals}
          height={"60vh"}
          totalAmount={totalAmounts?.payment || 0}
          page={page}
          rowsPerPage={count}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRows={total}
        />
      </Box>
    
    </Box>
  );
};

export default TransactionTab;